import React, { useEffect, useRef } from 'react';

import useTheme from '../context/hooks/useTheme';
import { ThemeKind } from '../context/ThemeContext';

// Mermaid is heavy (~3 MB). Load it lazily from a CDN when a post actually
// contains a diagram, so visitors to pages without diagrams don't pay the cost.
const MERMAID_CDN = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';

type MermaidApi = {
    initialize: (cfg: Record<string, unknown>) => void;
    render: (id: string, chart: string) => Promise<{ svg: string }>;
};

declare global {
    interface Window {
        mermaid?: MermaidApi;
    }
}

let mermaidPromise: Promise<MermaidApi> | null = null;
let counter = 0;

function loadMermaid(): Promise<MermaidApi> {
    if (mermaidPromise) return mermaidPromise;
    if (window.mermaid) {
        mermaidPromise = Promise.resolve(window.mermaid);
        return mermaidPromise;
    }
    mermaidPromise = new Promise<MermaidApi>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = MERMAID_CDN;
        script.async = true;
        script.onload = () => {
            if (window.mermaid) resolve(window.mermaid);
            else reject(new Error('mermaid loaded but window.mermaid is undefined'));
        };
        script.onerror = () => reject(new Error('failed to load mermaid from CDN'));
        document.head.appendChild(script);
    });
    return mermaidPromise;
}

interface MermaidDiagramProps {
    chart: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const renderToken = useRef(0);

    useEffect(() => {
        let cancelled = false;
        const token = ++renderToken.current;
        // Unique id per render to dodge mermaid's id-based DOM cache,
        // so theme switches force a fresh render.
        const id = `mermaid-${++counter}`;
        loadMermaid()
            .then((m) => {
                m.initialize({
                    startOnLoad: false,
                    theme: theme === ThemeKind.Dark ? 'dark' : 'neutral',
                    securityLevel: 'strict',
                    flowchart: { htmlLabels: true, curve: 'basis' },
                });
                return m.render(id, chart);
            })
            .then(({ svg }) => {
                if (cancelled || token !== renderToken.current) return;
                if (ref.current) ref.current.innerHTML = svg;
            })
            .catch((err) => {
                if (cancelled || token !== renderToken.current) return;
                if (ref.current) {
                    ref.current.innerHTML = `<pre class="text-accent dark:text-accent-soft text-[12px]">mermaid error: ${String(err)}</pre>`;
                }
            });
        return () => {
            cancelled = true;
        };
    }, [chart, theme]);

    return <div ref={ref} className="my-8 flex justify-center overflow-x-auto" />;
};

export default MermaidDiagram;
